import axios from 'axios';

// Sanitize: strip trailing dots (DNS root) and trailing slashes from env var
const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\.+\/*$/, '').replace(/\/+$/, '');

const crmAPI = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with requests
});

// Handle 401 errors by redirecting to login
// ONLY redirect if we're on admin/CRM routes (not portal, club, etc.)
crmAPI.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const pathname = window.location.pathname;

      // Don't redirect if already on any login page
      if (pathname.includes('/login')) {
        return Promise.reject(error);
      }

      // Don't redirect from portal, club, mill, or store routes - those have their own handlers
      if (pathname.startsWith('/portal') || pathname.startsWith('/club') ||
          pathname.startsWith('/mill') || pathname.startsWith('/store')) {
        return Promise.reject(error);
      }

      // Only redirect to /login from admin/CRM routes
      localStorage.removeItem('rbf_user_data');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// Customer CRM Interfaces
export interface CustomerCRMProfile {
  account_status: 'ACTIVE' | 'PROSPECT' | 'INACTIVE' | 'ON_HOLD';
  preferred_products: string;
  delivery_preferences: string;
  special_pricing_notes: string;
  assigned_caller: string;
  last_contact_date: string | null;
  next_follow_up_date: string | null;
  customer_since: string | null;
  internal_notes: string;
}

export interface InteractionCommitment {
  text?: string;
  due_date?: string | null;
}

/** A CRMTask raised from a call. CRMTask is the model the Tasks tab already renders -
 *  there is no separate task queue for calls. */
export interface InteractionTask {
  id: number;
  title: string;
  due_date: string | null;
  completed: boolean;
  assigned_to: string | null;
}

export interface CreatedTask extends InteractionTask {
  created: boolean;
  priority: string;
  customer_id: number | null;
  interaction_id: number | null;
  assigned_to_id: number | null;
  /** False when the call was taken on a shared line and nobody could be resolved - the task
   *  fell to whoever clicked, and the UI must say so rather than imply it knew. */
  assignee_resolved_from_call: boolean;
}

/** A row in the call-first Call Log. The full interaction shape plus who it was with,
 *  so a row can open in place using the same card the customer page renders. */
export interface CallLogRow extends CustomerInteraction {
  party_name: string;
  party_type: 'customer' | 'bc_member' | null;
  party_id: number | null;
  is_matched: boolean;
  /** True when external_name is a carrier placeholder (WIRELESS CALLER) rather than an
   *  actual party name - shown on the row, but never used to seed a Customer name. */
  name_is_generic: boolean;
}

export interface CallLogSummary {
  total: number;
  unmatched: number;
  follow_up: number;
  unrouted: number;
  fragments: number;
  internal?: number;
}

export interface CustomerInteraction {
  id: number;
  interaction_type: string;
  interaction_type_display: string;
  caller: string;
  notes: string;
  outcome?: string;
  interaction_date: string;
  related_order_id?: number | null;
  related_order_number?: string | null;

  // Call-transcript pipeline (RingCentral). A PHONE_CALL carries NO `notes` -
  // its content lives entirely in these fields, so a timeline that reads only
  // `notes` renders a blank card. All optional: hand-logged interactions and
  // older rows predate the pipeline.
  call_direction?: 'inbound' | 'outbound' | '';
  // The other party's number. Set on every pipeline call; on an UNMATCHED call (no customer,
  // no bc_member) it is the only identifying information the row carries.
  external_number_e164?: string;
  duration_seconds?: number | null;
  // One conversation split across transfer/conference legs. Rows stay separate (each owns a
  // recording_id, the pipeline's dedup key); the UI groups on this key.
  conversation_key?: string;
  leg_started_at?: string | null;
  attribution_method?: 'session' | 'sessionid' | 'peer_graph' | 'ambiguous' | 'unmatched' | '';
  // False when the recording holds no real conversation (announcement/hold/silence only).
  is_substantive?: boolean;
  summary?: string;
  transcription?: string;
  transcript_labeled?: string;
  topics?: string[];
  products?: string[];
  // The summarizer is asked for {text, due_date} but it is an LLM - a bare
  // string element is possible, so consumers must tolerate both.
  commitments?: Array<InteractionCommitment | string>;
  sentiment?: 'positive' | 'neutral' | 'negative' | '';
  follow_up_needed?: boolean;
  transcript_quality?: 'good' | 'fair' | 'poor' | '';
  // Whisper's mean avg_logprob: a LOG PROBABILITY, always <= 0, near 0 = confident. NOT a
  // percentage - rendering it as one produced "-60%".
  transcript_confidence?: number | null;
  // 0..1 of the audio the transcript actually spans. Below 1 means a partial decode.
  transcript_coverage?: number | null;
  transcript_purged?: boolean;
  is_voice_note?: boolean;
  pipeline_status?: string;
  summary_status?: string;
  /** Tasks already raised from this call, so a commitment cannot be actioned twice. */
  tasks?: InteractionTask[];
  /** When the follow-up alert went out. NULL with follow_up_needed set means the call
   *  reached nobody - a shared-line call that resolved to no staff member. */
  follow_up_notified_at?: string | null;
  /** Staff-to-staff call: every party is one of our own extensions. */
  is_internal?: boolean;
  /** RingCentral's own name for the other party, carried on the party payload alongside the
   *  number and unread until now. Either our uploaded directory or carrier caller ID. */
  external_name?: string;
  /** Who on OUR side took the call, resolved from the extension. Authoritative. */
  handled_by?: string;
  handled_by_id?: number | null;
  /** A transcript guess, shared office line only. Render as "likely X", never as fact -
   *  transcription is unreliable on names ("Brett" for Brad). */
  handled_by_inferred?: string;
  /** One sentence describing something broken that the call surfaced. */
  possible_issue?: string;
}

export interface CustomerOrder {
  id: number;
  order_number: string;
  order_date: string;
  status: string;
  total_amount: number;
  items: {
    formula_name: string;
    quantity: number;
    weight: number;
    total: number;
  }[];
}

export interface CustomerCRMBrief {
  id: number;
  name: string;
  customer_type: string;
  customer_type_display: string;
  contact_person: string;
  phone: string;
  city: string;
  state: string;
  is_active: boolean;
  account_status: string;
  last_contact_date: string | null;
  last_interaction?: {
    date: string;
    caller: string;
    type: string;
  } | null;
}

export interface CustomerCRMFull {
  id: number;
  name: string;
  customer_type: string;
  customer_type_display: string;
  contact_person: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  postal_code: string;
  is_active: boolean;
  portal_enabled: boolean;
  credit_limit: number | null;
  current_balance: number;
  outstanding_balance: number;
  payment_terms: string;
  payment_terms_start: string;
  payment_terms_display: string;
  is_over_credit_limit: boolean;
  created_at: string;
  crm_profile: CustomerCRMProfile;
  recent_orders: CustomerOrder[];
  recent_interactions: CustomerInteraction[];
  total_orders: number;
  total_interactions: number;
  // AR snapshot — shared shape with the main Customers page, served by
  // feedmill.customer_ar.get_customer_ar_data on the backend.
  invoices?: Array<{
    id: number;
    invoice_number: string;
    invoice_date: string | null;
    due_date: string | null;
    total_amount: string;
    balance_due: string;
    status: string;
    is_overdue: boolean;
    order_number?: string | null;
  }>;
  payments?: Array<{
    id: number;
    amount: string;
    payment_date: string | null;
    payment_method: string;
    invoice_number?: string | null;
    transaction_id?: string | null;
  }>;
  ar_summary?: {
    total_outstanding: string;
    overdue_count: number;
    overdue_amount: string;
  };
}

export interface CallerAssignment {
  name: string;
  allowed_types: string[];
}

export interface CustomerCRMStats {
  wholesale_customers: number;
  retail_customers: number;
  interactions_this_month: number;
  needs_contact: number;
  prospects: number;
}

// Bridge Club Intelligence (for RETAIL_PARTNER customers)
export interface BridgeClubIntelligence {
  customer_id: number;
  customer_name: string;
  members: {
    total: number;
    active_last_60_days: number;
  };
  purchases_last_90_days: {
    count: number;
    total_bags: number;
    total_revenue: number;
  };
  loyalty: {
    pending: number;
    completed: number;
  };
  analytics: {
    top_products: string[];
    predicted_weekly_demand: Record<string, number>;
    suggested_order: Record<string, any>;
    period: string;
  } | null;
}

// General Intelligence (for LOCAL_WILL_CALL, OTHER, CJ_CUSTOMER)
export interface GeneralIntelligence {
  customer_id: number;
  customer_name: string;
  orders: {
    total_count: number;
    total_revenue: number;
    last_order_date: string | null;
    recent_90_day_count: number;
  };
  interactions: {
    total: number;
    last_date: string | null;
    recent: {
      date: string;
      type: string;
      caller: string;
      notes_preview: string;
    }[];
  };
  account_health: {
    days_since_contact: number | null;
    next_follow_up: string | null;
    credit_limit: number | null;
    current_balance: number;
    utilization_percent: number | null;
  };
  preferred_products: string;
}

// Customer CRM API
export const customerCRMAPI = {
  // List customers for CRM
  getAll: (params?: {
    customer_type?: string;
    caller?: string;
    account_status?: string;
    search?: string;
    include_inactive?: boolean;
  }) => crmAPI.get<{ count: number; results: CustomerCRMBrief[] }>('/crm/customers/', { params }),

  // Get full customer CRM data
  getById: (id: number) => crmAPI.get<CustomerCRMFull>(`/crm/customers/${id}/`),

  // Log a conversation/interaction
  logInteraction: (customerId: number, data: {
    caller: string;
    notes: string;
    interaction_type?: string;
    outcome?: string;
  }) => crmAPI.post<{ success: boolean; id: number; interaction_date: string }>(`/crm/customers/${customerId}/log_interaction/`, data),

  // Get all interactions for a customer
  getInteractions: (customerId: number) => crmAPI.get<CustomerInteraction[]>(`/crm/customers/${customerId}/interactions/`),

  // Raise a CRMTask from a commitment on a call. Nothing is auto-created; this is the
  // confirm step. The server fills the assignee from the call when it can.
  createTaskFromCall: (interactionId: number, data: {
    title: string;
    due_date?: string | null;
    assigned_to_id?: number | null;
    priority?: string;
    description?: string;
  }) => crmAPI.post<CreatedTask>(`/crm/calls/${interactionId}/create-task/`, data),

  // Call-first log: every call, newest first, customer as a column rather than a
  // precondition. Unmatched calls have nowhere to appear in a customer-scoped view.
  getCalls: (params?: {
    match?: 'matched' | 'unmatched';
    direction?: 'inbound' | 'outbound';
    follow_up?: boolean;
    unrouted?: boolean;
    caller?: string;
    search?: string;
    include_fragments?: boolean;
    include_internal?: boolean;
  }) => crmAPI.get<{ count: number; results: CallLogRow[] }>('/crm/calls/', { params }),

  getCallSummary: () => crmAPI.get<CallLogSummary>('/crm/calls/summary/'),

  // Teach the matcher a number, and re-attribute every unmatched call on it.
  attachCustomer: (interactionId: number, customerId: number) =>
    crmAPI.post<{ customer_id: number; customer_name: string; interactions_attached: number[] }>(
      `/crm/calls/${interactionId}/attach-customer/`, { customer_id: customerId }),

  createCustomerFromCall: (interactionId: number, data: {
    name: string; contact_person?: string; email?: string; city?: string;
    state?: string; customer_type?: string;
  }) => crmAPI.post<{ customer_id: number; customer_name: string; customer_created: boolean }>(
    `/crm/calls/${interactionId}/create-customer/`, data),

  // Get all interactions for a Bridge Club member. CustomerInteraction is owned
  // by a Customer XOR a BridgeClubMember; BC members are the majority of inbound
  // callers, so their calls need their own timeline read.
  getBCMemberInteractions: (memberId: number) =>
    crmAPI.get<CustomerInteraction[]>(`/crm/bc-members/${memberId}/interactions/`),

  // Get order history for a customer
  getOrders: (customerId: number) => crmAPI.get<CustomerOrder[]>(`/crm/customers/${customerId}/orders/`),

  // Update CRM profile
  updateProfile: (customerId: number, data: Partial<CustomerCRMProfile>) =>
    crmAPI.patch<{ success: boolean }>(`/crm/customers/${customerId}/update_profile/`, data),

  // Get callers and their assignments
  getCallers: () => crmAPI.get<CallerAssignment[]>('/crm/customers/callers/'),

  // Get customer types
  getCustomerTypes: () => crmAPI.get<{ value: string; label: string }[]>('/crm/customers/customer_types/'),

  // Get CRM stats
  getStats: () => crmAPI.get<CustomerCRMStats>('/crm/customers/stats/'),

  // Get Bridge Club intelligence for a retail partner
  getBridgeClubIntelligence: (customerId: number) =>
    crmAPI.get<BridgeClubIntelligence>(`/crm/customers/${customerId}/bridge-club-intelligence/`),

  // Get general intelligence for non-farm, non-retail customers
  getGeneralIntelligence: (customerId: number) =>
    crmAPI.get<GeneralIntelligence>(`/crm/customers/${customerId}/general-intelligence/`),
};

export default customerCRMAPI;
